import React, { useState, useEffect, useMemo, useCallback } from "react";
import ManualMeditionModal from "./manual-medition-modal";
import ReactDOMServer from "react-dom/server";
import Swal from "sweetalert2";
import { FaCube, FaRegSquare, FaWindows, FaTimes } from "react-icons/fa";
import "../entries.css";
import PalletSelectionModal from "./pallet-selection-modal";
import axios from "axios";

function CubiscanForm({ master, subproduct, isConsolidated, showForm }) {
  // Estados principales
  const [showPopup, setShowPopup] = useState(false);
  const [showPopupTwo, setShowPopupTwo] = useState(false);
  const [showAlert, setShowAlert] = useState(false);
  const [masterData, setMasterData] = useState(null);
  const [houses, setHouses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cubiscan, setCubiscan] = useState({});
  const [selectedAuditorId, setSelectedAuditorId] = useState(null);
  const [tolerance, setTolerance] = useState(null);
  const [hasEvaluatedTolerance, setHasEvaluatedTolerance] = useState(false);
  const [isExcluded, setIsExcluded] = useState(false);

  // Memoizar valores importantes
  const masterInfo = useMemo(() => master?.resp || master, [master]);
  const masterId = useMemo(
    () => masterInfo?.id || masterInfo?.master_id,
    [masterInfo],
  );

  // Función para obtener datos - unificada
  const fetchData = useCallback(async (url, params) => {
    try {
      const res = await axios.get(url, {
        params,
        headers: {
          Authorization: `Bearer ${sessionStorage.getItem("token")}`,
        },
      });
      return res.data?.data;
    } catch (error) {
      console.error("Error fetching data:", error);
      return null;
    }
  }, []);

  // Función para parsear datos del cubiscan
  const parseCubiscanData = useCallback((cubiData, rows) => {
    const realVolumeArr = splitCubeVol(cubiData?.cubevol_gross, rows);
    const GROSS_FACTOR = 1.863;
    const grossVolumeArr = realVolumeArr.map((v) =>
      Number((v * GROSS_FACTOR).toFixed(2)),
    );

    return {
      grossWeightArr: splitByRows(cubiData?.weight_gross, rows),
      realWeightArr: splitByRows(cubiData?.weight_net, rows),
      realVolumeArr,
      grossVolumeArr,
    };
  }, []);

  // Cargar datos iniciales
  useEffect(() => {
    if (!masterId) return;
    setTolerance(null);

    if (isConsolidated) {
      loadConsolidatedData(masterId);
    } else {
      loadFormData(masterId);
    }
  }, [masterId, isConsolidated]);

  // Cargar datos de subproducto en vista consolidada
  useEffect(() => {
    if (isConsolidated && subproduct && masterId) {
      loadSubproductData(masterId, subproduct.id);
    }
  }, [subproduct, isConsolidated, masterId]);

  const loadConsolidatedData = useCallback(
    async (id) => {
      setLoading(true);
      const data = await fetchData(
        `${process.env.REACT_APP_URL_BACKEND_EXPO}/api/v1/exp_entries/cubiscan/parcialities`,
        { master_id: id, house_id: id },
      );
      setHouses(data?.data || []);
      setLoading(false);
    },
    [fetchData],
  );

  const loadSubproductData = useCallback(
    async (masterId, houseId) => {
      setLoading(true);
      const cubi = await fetchData(
        `${process.env.REACT_APP_URL_BACKEND_EXPO}/api/v1/exp_entries/cubiscan/parcialities`,
        { master_id: masterId, house_id: houseId },
      );

      if (cubi) {
        setMasterData(cubi?.resp || {});
        setHouses(cubi?.data || []);
        setCubiscan(cubi?.cubiscan || {});
      }
      setLoading(false);
    },
    [fetchData],
  );

  const loadFormData = useCallback(
    async (id) => {
      setLoading(true);
      const cubi = await fetchData(
        `${process.env.REACT_APP_URL_BACKEND_EXPO}/api/v1/exp_entries/cubiscan/form`,
        { master_id: id },
      );

      if (cubi) {
        setTolerance(cubi.tolerance ?? null);

        const masterInfo = {
          awb: cubi.awb,
          master_orig: cubi.master.origin,
          master_dest: cubi.master.destiny,
          master_merch: cubi.master.description,
          master_pieces: cubi.master.pieces,
          master_weight: cubi.master.weight,
          master_volume: cubi.master.volume,
          id: cubi.master.id,
        };
        setMasterData(masterInfo);

        const housesData = (cubi.data || []).map((h) => ({
          ...h,
          checkedGross: false,
          checkedReal: false,
          hasPallets: h.has_pallets !== false,
          isManual: h.medition_type === null,
        }));
        setHouses(housesData);

        if (cubi.cubiscan) {
          setCubiscan(parseCubiscanData(cubi.cubiscan, housesData.length));
        }
      }
      setLoading(false);
    },
    [fetchData, parseCubiscanData],
  );

  // Funciones auxiliares
  const splitByRows = useCallback((str, rows) => {
    if (!str || !rows) return [];
    const clean = str.replace(/\s+/g, "");
    const size = Math.floor(clean.length / rows);
    const result = [];
    let cursor = 0;

    for (let i = 0; i < rows; i++) {
      const part =
        i === rows - 1
          ? clean.substring(cursor)
          : clean.substring(cursor, cursor + size);
      result.push(Number(part));
      cursor += size;
    }
    return result;
  }, []);

  const splitCubeVol = useCallback((str, rows) => {
    if (!str || !rows) return [];
    const clean = str.replace(/\s+/g, "");
    const CHUNK_SIZE = 8;
    const result = [];

    for (let i = 0; i < rows; i++) {
      const start = i * CHUNK_SIZE;
      const part = clean.substring(start, start + CHUNK_SIZE);
      result.push(Number(part));
    }
    return result;
  }, []);

  // Handlers optimizados
  const handleUpdate = () => alert("Actualizar");

  const handleSave = () => {
    const hasSelection = houses.some((h) => h.checkedGross || h.checkedReal);
    if (!hasSelection) return;

    setHasEvaluatedTolerance(true);
    if (tolerance === 0) {
      setShowAlert(false);
    }
  };

  const handleCheck = useCallback((index, type) => {
    setHouses((prev) =>
      prev.map((row, i) => {
        if (i !== index) return row;
        return {
          ...row,
          checkedGross: type === "gross",
          checkedReal: type === "real",
        };
      }),
    );
    setShowAlert(false);
  }, []);

  const showConfirmationDialog = useCallback((html, callback) => {
    Swal.fire({
      title: "",
      html,
      showCancelButton: true,
      confirmButtonText: "Aceptar",
      cancelButtonText: "Cancelar",
      icon: "warning",
      customClass: {
        confirmButton: "btn-okk",
        cancelButton: "btn-canceel",
        popup: "alert-bg",
      },
    }).then((result) => {
      if (result.isConfirmed && callback) callback();
    });
  }, []);

  const handleExcludelMedition = useCallback(() => {
    if (!masterId) {
      Swal.fire("Error", "No se encontró Master ID.", "error");
      return;
    }

    const firstHtml = ReactDOMServer.renderToString(
      <div className="container-fluid">
        <p>¿Seguro que desea excluir la medición del Cubiscan?</p>
      </div>,
    );

    showConfirmationDialog(firstHtml, () => {
      const secondHtml = ReactDOMServer.renderToString(
        <div className="container-fluid">
          <p className="fw-bold">¿Seguro que quieres aceptar la guía?</p>
          <small style={{ color: "#DC3545", fontWeight: "700" }}>
            Esta acción es irreversible.
          </small>
        </div>,
      );

      showConfirmationDialog(secondHtml, async () => {
        try {
          const res = await axios.post(
            `${process.env.REACT_APP_URL_BACKEND_EXPO}/api/v1/exp_entries/cubiscan/excludeMedition`,
            { master_id: masterId, house_id: null },
            {
              headers: {
                Authorization: `Bearer ${sessionStorage.getItem("token")}`,
                "Content-Type": "application/json",
              },
            },
          );

          Swal.fire({
            icon: "success",
            title: "Éxito",
            text: "La medición del cubiscan fue excluida.",
          });

          setIsExcluded(true);
          setHasEvaluatedTolerance(true);
          setTolerance(0);
          loadFormData(masterId);
        } catch (error) {
          console.error("Error al excluir medición:", error);
          Swal.fire({
            icon: "error",
            title: "Error",
            text:
              error.response?.data?.message ||
              "Ocurrió un error al excluir la medición.",
          });
        }
      });
    });
  }, [masterId, showConfirmationDialog, loadFormData]);

  const handleDeleteFile = useCallback(
    async (fileId, fileName) => {
      const htmlConfirm = ReactDOMServer.renderToString(
        <div className="container-fluid">
          <p>¿Seguro que deseas eliminar este archivo del Cubiscan?</p>
          <p className="fw-bold">{fileName}</p>
          <small style={{ color: "#DC3545", fontWeight: "700" }}>
            Esta acción es irreversible.
          </small>
        </div>,
      );

      showConfirmationDialog(htmlConfirm, async () => {
        try {
          await axios.post(
            `${process.env.REACT_APP_URL_BACKEND_EXPO}/api/v1/exp_entries/cubiscan/deleteFile`,
            { id: fileId, name: fileName },
            {
              headers: {
                Authorization: `Bearer ${sessionStorage.getItem("token")}`,
                "Content-Type": "application/json",
              },
            },
          );

          Swal.fire({
            icon: "success",
            title: "Archivo eliminado",
            text: "El archivo fue eliminado correctamente.",
          });

          window.location.reload();
        } catch (error) {
          console.error("Error al eliminar archivo:", error);
          Swal.fire({
            icon: "error",
            title: "Error",
            text:
              error.response?.data?.message ||
              "No se pudo eliminar el archivo.",
          });
        }
      });
    },
    [showConfirmationDialog],
  );

  const handleGetTicket = useCallback(async () => {
    if (!masterId) {
      return Swal.fire("Error", "No se encontró Master ID.", "error");
    }

    if (tolerance === null) {
      return Swal.fire(
        "Atención",
        "La medición aún no ha sido evaluada.",
        "warning",
      );
    }

    try {
      await axios.get(
        `${process.env.REACT_APP_URL_BACKEND_EXPO}/api/v1/exp_entries/cubiscan/getTicket?id=${masterId}`,
        {
          headers: {
            Authorization: `Bearer ${sessionStorage.getItem("token")}`,
          },
        },
      );

      Swal.fire({
        icon: "success",
        title: "Ticket generado",
        text: "El ticket se generó correctamente.",
      });
    } catch (error) {
      console.error("Error al generar ticket:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error.response?.data?.message || "No se pudo generar el ticket.",
      });
    }
  }, [masterId, tolerance]);

  const handleAcceptGuide = useCallback(async () => {
    const selected = houses.filter((h) => h.checkedGross || h.checkedReal);
    if (selected.length === 0) {
      return Swal.fire(
        "Error",
        "Debe seleccionar una medición válida",
        "error",
      );
    }

    try {
      const payload = {
        master_id: masterId,
        house_id: null,
        medType: selected.map((h) => (h.checkedGross ? 1 : 2)),
        id: selected.map((h) => h.id_exp_auditor || h.id),
        client_autorization: true,
      };

      await axios.post(
        `${process.env.REACT_APP_URL_BACKEND_EXPO}/api/v1/exp_entries/cubiscan/save`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${sessionStorage.getItem("token")}`,
            "Content-Type": "application/json",
          },
        },
      );

      Swal.fire({
        icon: "success",
        title: "Guía aceptada",
        text: "La guía ha sido aceptada correctamente.",
      });

      window.location.reload();
    } catch (error) {
      console.error("Error al aceptar guía:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error.response?.data?.message || "No se pudo aceptar la guía.",
      });
    }
  }, [houses, masterId]);

  // Funciones de utilidad
  const safeValue = useCallback((v, d = "N/A") => v ?? d, []);

  // Renderizar tabla de subproductos para vista consolidada
  const renderConsolidatedTable = useCallback(() => {
    if (!isConsolidated || subproduct) return null;

    return (
      <div className="mt-5">
        <table className="table" style={{ width: "70%", margin: "0 auto" }}>
          <thead className="text-center">
            <tr>
              <th style={{ backgroundColor: "#020C41", color: "white" }}>
                HBS
              </th>
              <th style={{ backgroundColor: "#020C41", color: "white" }}>
                Fecha
              </th>
              <th style={{ backgroundColor: "#020C41", color: "white" }}>
                Piezas
              </th>
              <th style={{ backgroundColor: "#020C41", color: "white" }}>
                Peso
              </th>
              <th style={{ backgroundColor: "#020C41", color: "white" }}>
                Acciones
              </th>
            </tr>
          </thead>
          <tbody className="text-center">
            {master?.data?.map((sub, index) => (
              <tr key={index}>
                <td>{safeValue(sub.hbs)}</td>
                <td>{safeValue(sub.date)}</td>
                <td>{safeValue(sub.pieces)}</td>
                <td>{safeValue(sub.weight)}</td>
                <td>
                  <button
                    className="btn btn-primary btn-sm"
                    onClick={() => showForm(sub, "cubiscan")}
                  >
                    ➜
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {(!master?.data || master.data.length === 0) && (
          <div className="text-center mt-3">
            <p className="text-muted">No hay subproductos disponibles</p>
          </div>
        )}
      </div>
    );
  }, [isConsolidated, subproduct, master?.data, showForm, safeValue]);

  // Renderizar información del master
  const renderMasterInfo = useCallback(() => {
    if (isConsolidated && !subproduct) return null;

    return (
      <>
        <div className="row">
          <div className="col-3">
            <p>
              <span className="fw-bold">Master:</span> {masterData?.awb}
            </p>
          </div>
          <div className="col-3">
            <p>
              <span className="fw-bold">Origen:</span> {masterData?.master_orig}
            </p>
          </div>
          <div className="col-3">
            <p>
              <span className="fw-bold">Destino:</span>{" "}
              {masterData?.master_dest}
            </p>
          </div>
          <div className="col-3">
            <p>
              <span className="fw-bold">Tipo de mercancía:</span>{" "}
              {masterData?.master_merch}
            </p>
          </div>
        </div>
        <div className="row">
          <div className="col-3">
            <p>
              <span className="fw-bold">Piezas Master:</span>{" "}
              {masterData?.master_pieces}
            </p>
          </div>
          <div className="col-3">
            <p>
              <span className="fw-bold">Peso Master:</span>{" "}
              {masterData?.master_weight}
            </p>
          </div>
          <div className="col-3">
            <p>
              <span className="fw-bold">Volúmen Master:</span>{" "}
              {masterData?.master_volume}
            </p>
          </div>
        </div>
      </>
    );
  }, [isConsolidated, subproduct, masterData]);

  // Renderizar tabla de mediciones
  const renderMeasurementsTable = useCallback(() => {
    if (isConsolidated && !subproduct) return null;

    return (
      <table className="table table-striped mt-3">
        <thead className="text-center">
          <tr>
            <th style={{ backgroundColor: "#020C41", color: "white" }}>
              Nombre archivo
            </th>
            <th style={{ backgroundColor: "#020C41", color: "white" }}>
              Gross Weight <FaCube />
            </th>
            <th style={{ backgroundColor: "#020C41", color: "white" }}>
              Gross Volume <FaCube />
            </th>
            <th style={{ backgroundColor: "#020C41", color: "white" }}>
              Real Weight <FaRegSquare />
            </th>
            <th style={{ backgroundColor: "#020C41", color: "white" }}>
              Real Volume <FaRegSquare />
            </th>
            <th style={{ backgroundColor: "#020C41", color: "white" }}>
              Acciones
            </th>
          </tr>
        </thead>
        <tbody className="text-center">
          {houses.length === 0 ? (
            <tr>
              <td colSpan="6">No hay mediciones registradas</td>
            </tr>
          ) : (
            houses.map((h, index) => {
              const canOpenPallets =
                !isExcluded &&
                h.checkedReal &&
                !h.checkedGross &&
                (h.hasPallets || h.isManual || h.medition_type === null);
              const canDelete =
                !isExcluded && !h.checkedGross && !h.checkedReal;

              return (
                <tr key={h.id_exp_auditor || index}>
                  <td>{h.file_name || `Archivo ${index + 1}`}</td>
                  <td>
                    {h.gross_weight ?? cubiscan.grossWeightArr?.[index] ?? "--"}
                  </td>
                  <td>
                    {h.gross_volume != null
                      ? `${(h.gross_volume / 1000).toFixed(5)} m3`
                      : cubiscan.grossVolumeArr?.[index] != null
                        ? `${(cubiscan.grossVolumeArr[index] / 1000).toFixed(5)} m3`
                        : "--"}
                  </td>
                  <td>
                    {h.real_weight ?? cubiscan.realWeightArr?.[index] ?? "--"}
                  </td>
                  <td>
                    {h.real_volume != null
                      ? `${(h.real_volume / 1000).toFixed(5)} m3`
                      : cubiscan.realVolumeArr?.[index] != null
                        ? `${(cubiscan.realVolumeArr[index] / 1000).toFixed(5)} m3`
                        : "--"}
                  </td>
                  <td>
                    <FaCube
                      size={20}
                      style={{ marginRight: "5px", color: "#020C41" }}
                    />
                    <input
                      className="form-check-input"
                      type="checkbox"
                      checked={h.checkedGross}
                      disabled={isExcluded}
                      onChange={() => handleCheck(index, "gross")}
                      style={{ marginRight: "20px" }}
                    />
                    <FaRegSquare
                      size={20}
                      style={{ marginRight: "5px", color: "#020C41" }}
                    />
                    <input
                      className="form-check-input"
                      type="checkbox"
                      checked={h.checkedReal}
                      disabled={isExcluded}
                      onChange={() => handleCheck(index, "real")}
                      style={{ marginRight: "15px" }}
                    />
                    <button
                      style={{ border: "none" }}
                      onClick={() => {
                        setSelectedAuditorId(h.id_exp_auditor);
                        setShowPopupTwo(true);
                      }}
                      disabled={!canOpenPallets}
                    >
                      <FaWindows
                        size={20}
                        style={{
                          transform: "rotate(90deg)",
                          opacity: canOpenPallets ? 1 : 0.4,
                          cursor: canOpenPallets ? "pointer" : "not-allowed",
                        }}
                      />
                    </button>
                    <button
                      onClick={() =>
                        handleDeleteFile(h.id_exp_auditor || h.id, h.file_name)
                      }
                      disabled={!canDelete}
                      style={{
                        background: "none",
                        border: "none",
                        cursor: canDelete ? "pointer" : "not-allowed",
                        opacity: canDelete ? 1 : 0.4,
                      }}
                    >
                      <FaTimes size={20} style={{ color: "#020C41" }} />
                    </button>
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    );
  }, [
    houses,
    cubiscan,
    isExcluded,
    handleCheck,
    handleDeleteFile,
    isConsolidated,
    subproduct,
  ]);

  // Renderizar alertas de tolerancia
  const renderToleranceAlerts = useCallback(() => {
    if (isConsolidated) return null;

    return (
      <>
        {!isExcluded && hasEvaluatedTolerance && tolerance === 0 && (
          <div className="alert alert-success mt-3 text-center" role="alert">
            <p>Cumplió con el rango:</p>
            <p>Valores aceptados, se cobra conforme los datos de la guía.</p>
          </div>
        )}

        {hasEvaluatedTolerance && (tolerance === 1 || tolerance === -1) && (
          <div className="alert alert-danger mt-3 text-center" role="alert">
            <p className="fw-bold">No cumplió con el rango:</p>
            <p>
              Los valores exceden el rango permitido, favor de validar el
              proceso correspondiente.
            </p>
          </div>
        )}

        {showAlert && (
          <div className="alert alert-danger" role="alert">
            Se deben seleccionar todos los tipos de medición o eliminar las
            mediciones que no se utilizarán.
          </div>
        )}
      </>
    );
  }, [isConsolidated, isExcluded, hasEvaluatedTolerance, tolerance, showAlert]);

  // Renderizar botones de acción
  const renderActionButtons = useCallback(() => {
    if (isConsolidated) return null;

    const showExcludeButton =
      !isExcluded &&
      (tolerance === null || tolerance === 1 || tolerance === -1);
    const showAcceptGuideButton = !isExcluded && tolerance === 1;

    return (
      <div className="row">
        <div className="col-6">
          {showExcludeButton && (
            <button className="btn btn-danger" onClick={handleExcludelMedition}>
              Excluir medición cubiscan
            </button>
          )}
        </div>
        <div className="col-6 text-end">
          {!isExcluded && (
            <button className="btnn" onClick={() => setShowPopup(true)}>
              Medición manual
            </button>
          )}
          {showAcceptGuideButton && (
            <button className="btnn me-2" onClick={handleAcceptGuide}>
              Aceptar guía
            </button>
          )}
        </div>
      </div>
    );
  }, [
    isConsolidated,
    isExcluded,
    tolerance,
    handleExcludelMedition,
    handleAcceptGuide,
  ]);

  // Renderizar botones de control
  const renderControlButtons = useCallback(() => {
    if (isConsolidated) return null;

    return (
      <div className="row">
        <div className="col mt-4 d-flex justify-content-end gap-3">
          <button className="btnn" onClick={handleUpdate}>
            Actualizar
          </button>
          <button
            className="btnn"
            onClick={handleSave}
            disabled={hasEvaluatedTolerance && tolerance !== 0}
          >
            Guardar
          </button>
          <button className="btnn" onClick={handleGetTicket}>
            Ticket
          </button>
        </div>
      </div>
    );
  }, [isConsolidated, hasEvaluatedTolerance, tolerance, handleGetTicket]);

  // Renderizar loading
  if (loading) {
    return (
      <div className="text-center p-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Cargando...</span>
        </div>
        <p className="mt-3">Cargando datos...</p>
      </div>
    );
  }

  // Renderizar tabla consolidada
  if (isConsolidated && !subproduct) {
    return renderConsolidatedTable();
  }

  return (
    <>
      <div className="container-fluid">
        {renderMasterInfo()}
        {renderActionButtons()}
        {renderMeasurementsTable()}
        {renderToleranceAlerts()}
        {renderControlButtons()}
      </div>

      {showPopup && (
        <ManualMeditionModal
          master={master}
          onClose={() => setShowPopup(false)}
          onManualAdded={(row) => {
            setHouses((prev) => [
              ...prev,
              {
                ...row,
                checkedGross: false,
                checkedReal: false,
                hasPallets: true,
                isManual: true,
              },
            ]);
          }}
        />
      )}

      {showPopupTwo && (
        <PalletSelectionModal
          onClose={() => setShowPopupTwo(false)}
          idExpAuditor={selectedAuditorId}
        />
      )}
    </>
  );
}

export default CubiscanForm;
